/**
 * Install.
 *
 * **Sourced.** Every question below is from `Installation_Form_FIXED.qsf`
 * (`SurveyID: SV_8G1669b6MfCq5IG`, "Installation Form", last modified 2026-09-01), the export the
 * owner supplied directly. Question text, choice lists, required-ness and display logic are taken
 * from that file; `sourceTag` carries the original `DataExportTag` so any wording can be checked
 * against the source. Section order follows the survey's own Survey Flow, not its block order —
 * the flow runs Default → Asset Management → "Installation if not good" → "installation if good"
 * → Provisioning, which is not the order the blocks appear in the export.
 *
 * Three deliberate departures from the source, each for a stated reason:
 *
 *  - **The eleven parity questions are prefilled, not asked.** The survey receives `KioskID`,
 *    `LockerName`, `Layout`, `Generation`, `Color`, `Placement`, `LockerType`, `LocationName`,
 *    `countrycode`, `InterventionID`, `PIID` and `NumberOfModule` as URL parameters and then asks
 *    the technician to retype eight of them anyway. They are prefilled here with provenance, and
 *    remain overridable, because the confirmation signal is the point.
 *
 *  - **Two visit-end-time questions collapse into one.** The source has `Q55` and `Q56` because
 *    a response can terminate on either the install or the provisioning branch. One field with
 *    the union of both conditions is the same data with one fewer way to disagree.
 *
 *  - **`Q6` "What is the technician's name?" is removed rather than prefilled.** Identity comes
 *    from the verified token and the backend sets `Submitted_By_Email__c` server-side.
 *    `InstallersTech_Name__c` is on the object but is excluded from the write path by decision —
 *    a client-asserted technician name is not evidence of anything.
 *
 * The 20 `TrashUnused` questions in the source's Trash block are not reproduced.
 *
 * **`Q102` appears on three separate questions here, and that is faithful reporting rather than a
 * copy-paste slip.** `damage_description`, `shelter_or_lightbox` and `provisioning_hardware_other`
 * all carry `sourceTag: 'Q102'` because `Installation_Form_FIXED.qsf` gives all three the same
 * `DataExportTag`. That reuse was independently identified as a defect in the upstream Qualtrics
 * survey, and unlike the maintenance duplicates these three questions **co-occur**: an outdoor
 * pre-Gen-5 install with visible damage and a Hardware-Other provisioning failure shows all three
 * at once, so in the source they collide in one export column. The field **ids** here are distinct,
 * so nothing overwrites anything in this app; only the pointer back to the source repeats.
 * Inventing three unique-looking tags would make this file look correct while erasing the only
 * evidence of the upstream problem, so the tag is kept and the collision is disclosed instead.
 *
 * **Salesforce mapping is the weak point of this schema and is marked as such.** The install
 * survey's Qualtrics→Salesforce workflow was never captured: the QSF has no `OCAC` elements
 * (modern Workflows do not appear in exports) and the Workflows API returned no entry for
 * `SV_8G1669b6MfCq5IG`. Fields below marked `verified` were matched by name against the
 * `Work_Submission__c` describe in `discovery/salesforce/field-dumps.txt`; fields marked
 * `unverified` are name-matched guesses with no describe hit and must not be blind-written.
 */

import type { Field, FormSchema, Section } from '../types.ts';

const WS = 'Work_Submission__c';
const DESCRIBE = 'discovery/salesforce/field-dumps.txt (Work_Submission__c describe, 742 fields)';
const NO_WORKFLOW =
  'Question→field mapping for this survey was never captured (discovery/qualtrics/13-INSTALL-FORM.md §8). Field matched by name against the describe only.';

/** A field that exists on the object under this exact API name. */
function maps(field: string, type: string, caveat?: string) {
  return {
    object: WS,
    field,
    type,
    confidence: 'verified' as const,
    evidence: `${DESCRIBE}. ${NO_WORKFLOW}`,
    ...(caveat !== undefined ? { caveat } : {}),
  };
}

/** A field whose API name is a guess. Nothing may write these without checking first. */
function guess(field: string, why: string) {
  return {
    object: WS,
    field,
    confidence: 'unverified' as const,
    evidence: `No describe hit for this API name. ${why} ${NO_WORKFLOW}`,
  };
}

/**
 * The 20 serial + 20 photo pairs, generated from the schema rather than typed out.
 *
 * The source survey has these as forty hand-written questions each gated on
 * `NumberOfModule >= n`. Generating them keeps the same shape while making it impossible for
 * pair 14 to differ from pair 13 by accident — which in the source it does: `Module14_Serial`
 * carries a second clause, `NumberOfModule Is Equal to TRUE`, that can never be true and so
 * makes module 14's serial permanently unreachable.
 *
 * `NumberOfModule` becomes `ctx.moduleCount`, derived from `Hub_Size__c` rather than passed in.
 */
function moduleSerialFields(): Field[] {
  const out: Field[] = [];
  for (let n = 1; n <= 20; n += 1) {
    const pad = String(n).padStart(2, '0');
    const showIf = { field: 'ctx.moduleCount', op: 'gte' as const, value: n };
    out.push({
      id: `module${pad}_serial`,
      kind: 'serial',
      label:
        n === 1
          ? 'Serial number for module 1 (on the top or back of the module: "A", "S", "Q" or "M")'
          : `Serial number for module ${n}`,
      ...(n === 1
        ? { help: 'Scan the barcode on the label if you can. Typing it is the fallback, not the default.' }
        : {}),
      required: true,
      showIf,
      // Bound to the asset records, which are marked untrusted: roughly 60% of stored serials are
      // migration placeholders. The stored value is shown beside the field to compare against and
      // is never adopted as the answer, and the accounting counts this as asked, not prefilled.
      prefill: { source: 'module.serials' },
      validate: { minLength: 4, maxLength: 100 },
      sourceTag: `Module${pad}_Serial`,
      maps: maps(
        `Serial_number_per_module_${n}__c`,
        'string(100)',
        'string(100) per the describe, but infra/src/domain/form-schema.ts declares maxLength 255. The stricter limit is enforced here.',
      ),
    });
    out.push({
      id: `module${pad}_photo`,
      kind: 'photo',
      label: `Photo of the serial number label on module ${n}`,
      help: 'The scan gives queryable data; the photo is the evidence when the scan is later disputed. Both, deliberately.',
      required: true,
      showIf,
      validate: { minPhotos: 1, maxPhotos: 2 },
      sourceTag: `Module${pad}_Photo`,
      maps: maps(`Picture_Serial_number_of_module_${n}__c`, 'url(255)'),
    });
  }
  return out;
}

const IS_PROVISIONING_ONLY = { field: 'intervention_type', op: 'eq' as const, value: 'Provisioning' };
const DOES_INSTALL = {
  field: 'intervention_type',
  op: 'in' as const,
  value: ['Installation', 'Installation & Provisioning'],
};
const DOES_PROVISIONING = {
  field: 'intervention_type',
  op: 'in' as const,
  value: ['Installation & Provisioning', 'Provisioning'],
};

const identity: Section = {
  id: 'visit',
  title: 'This visit',
  subtitle:
    'Everything here except the intervention type and the start time is already on the record. Check it and correct anything wrong — a correction is more valuable than a confirmation.',
  fields: [
    {
      id: 'intervention_type',
      kind: 'single',
      label: 'What type of intervention are you doing?',
      options: ['Installation', 'Installation & Provisioning', 'Provisioning'],
      required: true,
      sourceTag: 'Q1',
      // Derived from Intervention_Type__c plus the boolean Provisioned_With_Installation__c,
      // because the 43-value type picklist alone cannot express "Installation & Provisioning".
      // This answer reroutes the entire form, so it is prefilled but stays the first question on
      // screen and is overridable — a wrong derivation must be cheap to correct, not invisible.
      prefill: { source: 'intervention.visitKind', overrideHint: 'Change it if the dispatch is wrong about what you are here to do.' },
      help: 'From the dispatch. Choosing Provisioning skips every install question.',
      maps: maps('Installation_form_submission_status__c', 'picklist Yes|No', 'Poor fit — the object has no intervention-type field. Most likely this routes the record type rather than landing in a field. Treat the destination record type as the real carrier of this answer.'),
    },
    {
      id: 'visit_start',
      kind: 'time',
      label: 'Visit start time (HH:MM, 24-hour)',
      required: true,
      prefill: { source: 'visit.startTime', overrideHint: 'Correct it if you started before opening the app.' },
      sourceTag: 'Q22',
      validate: { pattern: '([01][0-9]|2[0-3]):[0-5][0-9]', patternMessage: 'Use HH:MM, 24-hour' },
      maps: maps('Visit_Start_Time__c', 'string(30)'),
    },
    {
      id: 'case_id',
      kind: 'text',
      label: 'Case ID',
      help:
        'The survey passes this in as a parameter and then overwrites it with whatever the technician types. That is a deliberate choice by its builders — they did not trust the passed value — so it is prefilled and overridable here rather than deleted.',
      required: true,
      prefill: { source: 'case.number' },
      sourceTag: 'Q103',
      maps: maps('Case__c', 'reference to Case', 'Refused by the write allowlist: infra/src/writer/field-policy.ts lists Case__c under UNCORRECTED_LINK_FIELDS. Carried for context, not for writing.'),
    },
    {
      id: 'location_name',
      kind: 'text',
      label: 'Name of the location where you are installing the locker',
      required: true,
      /*
       * ⚠️ REPOINTED from `kiosk.siteName` on 2026-09-03, on the owner's requirement that this
       * value *"should pass from the intervention"*. Three reasons, in order of weight:
       *
       *  1. `kiosk.siteName` reads `OpportunityLineItem.Address_Line_1__c` — an ADDRESS LINE. The
       *     question asks for a NAME. `Intervention__c.Account_Name__c` is the host account's
       *     name: "West Hartford Collection", "Penny - WIEN - EMICHGASSE 6", "GALP - Betxi".
       *  2. Measured 98.57% on the actionable set (Dev1, 2026-09-03), and 97.0% on Survey-type
       *     interventions, 50.7% of which have no kiosk at all — so on a survey the kiosk read
       *     cannot answer this and the intervention can.
       *  3. The intervention is the entry point (PRD §4.3), so it is available strictly earlier;
       *     the kiosk read additionally sits behind a harder blocker, because
       *     `OpportunityLineItem` has no `ObjectPermissions` row anywhere in the org.
       *
       * `kiosk.siteName` stays in the catalog and stays bound in `post-install.ts` and
       * `site-prep.ts`, which is deliberate rather than an oversight: those forms are about a
       * kiosk that already exists at an address.
       */
      prefill: { source: 'intervention.locationName' },
      sourceTag: 'Q2',
      maps: guess('Location_Name__c', 'Prefilled from Intervention__c.Account_Name__c, the host account name, which is read-only there.'),
    },
    {
      id: 'kiosk_id',
      kind: 'text',
      label: 'Kiosk ID',
      required: true,
      prefill: { source: 'kiosk.id', overrideHint: 'Only if the dispatch sent you to the wrong kiosk.' },
      sourceTag: 'Q3',
      maps: maps('Kiosk_ID__c', 'string(255)', 'Not an external id on this object, unlike the 8-char OpportunityLineItem.Kiosk_ID__c. The platform will accept anything.'),
    },
    {
      id: 'locker_name',
      kind: 'text',
      label: 'Locker name',
      required: true,
      prefill: { source: 'kiosk.name' },
      sourceTag: 'Q4',
      maps: maps('Locker_Name__c', 'string(255)'),
    },
    {
      id: 'external_id',
      kind: 'text',
      label: 'External ID / installation ID',
      prefill: { source: 'intervention.supplierWorkOrderId' },
      sourceTag: 'Q5',
      maps: maps('Vendor_reference_number__c', 'string(255)', 'On the write allowlist in infra/src/writer/field-policy.ts.'),
    },
    {
      id: 'locker_type',
      kind: 'single',
      label: 'Is the location residential or commercial?',
      options: ['Commercial', 'Residential'],
      required: true,
      prefill: { source: 'intervention.lockerType' },
      sourceTag: 'Q7',
      maps: maps('Locker_Type__c', 'picklist', 'The picklist conflates product line and occupancy: Amazon Locker, Dobby, Indoor, Odin, Outdoor, Commercial, Residential. Only the last two are meant here.'),
    },
    {
      id: 'layout',
      kind: 'text',
      label: 'Locker layout',
      help: 'One character per column, left to right: S starter, A adder, Q quarter, M Gen-4 module.',
      required: true,
      prefill: { source: 'kiosk.layout', overrideHint: 'Correct it if the as-built layout differs from the order.' },
      sourceTag: 'Q8',
      validate: { minLength: 1, maxLength: 255 },
      maps: maps('Locker_Layout__c', 'picklist (15 values)', 'A restricted 15-value picklist (S, AS, SA, ASA, … MMMMM) on a field the source survey collects as free text. Any layout outside those 15 will not write. Needs either a normalisation map or the picklist widened.'),
    },
    {
      id: 'generation',
      kind: 'single',
      label: 'Locker generation',
      options: ['2.5', '3.0', '4.0', '5.0'],
      required: true,
      prefill: { source: 'kiosk.generation' },
      sourceTag: 'Q9',
      maps: maps('Locker_Generation__c', 'picklist 2|2.5|3|4|5', 'Value sets disagree across objects: OpportunityLineItem holds 2.0/2.5/3.0/GEN_3.0/4.0/5.0, Work_Submission__c holds 2/2.5/3/4/5. Normalise before writing — 3.0 will not match 3.'),
    },
    {
      id: 'locker_color',
      kind: 'single',
      label: 'Locker colour',
      options: ['Prime Blue', 'Yellow', 'Squid Ink', 'Grey'],
      required: true,
      prefill: { source: 'kiosk.color', overrideHint: 'The record is 90.5% populated, and a wrong colour is worth reporting.' },
      sourceTag: 'Q10',
      maps: maps('Locker_Color__c', 'picklist', 'Work_Submission__c spells it "Grey"; OpportunityLineItem spells it "Gray". Both exist. Do not round-trip without normalising.'),
    },
    {
      id: 'placement',
      kind: 'single',
      label: 'Is the locker being installed indoor or outdoor?',
      options: ['Indoor', 'Outdoor'],
      required: true,
      prefill: { source: 'kiosk.placement' },
      sourceTag: 'Q11',
      help: 'This gates six downstream photo questions, so prefilling it also collapses branching.',
      maps: maps('Indoor_outdoor__c', 'picklist Indoor|Outdoor', 'C-68 hazard: on a Maintenance record, Indoor_outdoor__c = Outdoor together with Anchor_Type__c = No fires Review_Anchoring_Task in an unguarded flow and sends real outbound email from no-reply-support@the-hub.amazon.com. Harmless on an Installation record, but the pair must never be written speculatively.'),
    },
    {
      id: 'module_count_note',
      kind: 'note',
      label:
        'How many modules this locker has is derived from Hub_Size__c, not asked. That derivation gates the serial-capture block below, exactly as the source survey gates it on the NumberOfModule parameter.',
    },
  ],
};

const beforeWork: Section = {
  id: 'before',
  title: 'Before you start',
  showIf: DOES_INSTALL,
  fields: [
    {
      id: 'area_before_photo',
      kind: 'photo',
      label: 'Photo of the area before the installation',
      required: true,
      validate: { minPhotos: 1, maxPhotos: 3 },
      sourceTag: 'Q16',
      maps: maps('Location_Photo_Link__c', 'textarea(5000)'),
    },
    {
      id: 'spoke_to_contact',
      kind: 'yesno',
      label: 'Have you spoken to the onsite contact before the installation?',
      required: true,
      sourceTag: 'Q12',
      maps: maps('Spoke_with_onsite_contact__c', 'picklist Yes|No'),
    },
    {
      id: 'contact_name',
      kind: 'text',
      label: "What is the onsite contact's name?",
      required: true,
      showIf: { field: 'spoke_to_contact', op: 'eq', value: true },
      sourceTag: 'Q13',
      maps: guess('Onsite_Contact_Name__c', 'The object carries Property_Contact_Email__c but no contact-name field was found in the describe.'),
    },
    {
      id: 'no_contact_why',
      kind: 'textarea',
      label: "Why haven't you spoken to the onsite contact?",
      required: true,
      showIf: { field: 'spoke_to_contact', op: 'eq', value: false },
      sourceTag: 'Q14',
      /*
       * ⚠️ REPOINTED 2026-09-03. This used to map to `Additional_Comments__c` — the same target as
       * `comments` in the Close out section. `comments` has no `showIf` and its section has none
       * either, so it is visible on EVERY install; this field is visible whenever
       * `spoke_to_contact` is No. Both are therefore on screen together on any install where the
       * technician did not reach the onsite contact, and one of the two answers was silently
       * discarded on write — last one wins. A test now proves the co-occurrence rather than
       * reasoning about it: see `test/forms/schema-defects.test.ts`.
       *
       * `Reason_for_not_speaking_with_onsite_cont__c` is the correct home and it already existed.
       * VERIFIED against the Dev1 describe on 2026-09-03: type `textarea`, length **1000**,
       * createable and updateable, label "Reason for not speaking with onsite cont" — which is this
       * question. `maxLength` is 1000 to match, down from the 3000 that was inherited from the old
       * target; 3000 characters into a 1000-character field truncates or 400s at the API.
       */
      validate: { maxLength: 1000 },
      maps: {
        object: WS,
        field: 'Reason_for_not_speaking_with_onsite_cont__c',
        type: 'textarea(1000)',
        confidence: 'verified',
        evidence:
          'Dev1 Work_Submission__c describe read 2026-09-03 via `sf sobject describe --sobject Work_Submission__c --target-org dev1-sandbox` (751 fields): name Reason_for_not_speaking_with_onsite_cont__c, type textarea, length 1000, createable true, updateable true, label "Reason for not speaking with onsite cont". ' +
          NO_WORKFLOW,
        caveat:
          'Repointed off Additional_Comments__c, which this question shared with `comments` in Close out — a same-schema collision where both fields are visible together and the later write won. Not among the fields any captured workflow writes, so the mapping is name-and-label matched against a live describe, not observed in an integration.',
      },
    },
    {
      id: 'install_guide_photo',
      kind: 'photo',
      label: 'Photo of the install guide / required locker placement',
      showIf: DOES_INSTALL,
      sourceTag: 'Q38',
      maps: maps('Install_Guide_Link__c', 'url(255)', 'The record already carries an install-guide link at 56.1% fill; this is a photo of the printed sheet, which is a different artefact. Likely the wrong home for it.'),
    },
    {
      id: 'delivery_receipt_photo',
      kind: 'photo',
      label: 'Photo of the delivery receipt',
      showIf: DOES_INSTALL,
      sourceTag: 'Q52',
      maps: guess('Delivery_Receipt_Photo__c', 'No delivery-receipt field found in the describe.'),
    },
  ],
};

/** The survey's "Installation if not good" block. Answering No to Q19 ends the response. */
const blocked: Section = {
  id: 'blocked',
  title: 'Did it go to plan?',
  showIf: DOES_INSTALL,
  fields: [
    {
      id: 'as_per_survey',
      kind: 'yesno',
      label: 'Was the locker installed as per the survey?',
      required: true,
      sourceTag: 'Q17',
      maps: guess('Installed_As_Per_Survey__c', 'No matching field found in the describe.'),
    },
    {
      id: 'not_as_per_survey_why',
      kind: 'textarea',
      label: 'If not, why was it not installed as per the survey?',
      required: true,
      showIf: { field: 'as_per_survey', op: 'eq', value: false },
      sourceTag: 'Q18',
      validate: { maxLength: 3000 },
      maps: maps('Additional_Comments_if_needed__c', 'textarea(20000)'),
    },
    {
      id: 'could_continue',
      kind: 'yesno',
      label: 'Were you able to continue the installation?',
      required: true,
      showIf: { field: 'as_per_survey', op: 'eq', value: false },
      sourceTag: 'Q19',
      maps: guess('Able_To_Continue_Installation__c', 'No matching field found in the describe.'),
    },
    {
      id: 'blocked_photo',
      kind: 'photo',
      label: 'If you could not continue the installation, provide a picture',
      required: true,
      showIf: { field: 'could_continue', op: 'eq', value: false },
      sourceTag: 'Q20',
      /*
       * Shares `Additional_Photos_if_needed__c` with `photo_additional` in the Photos section, and
       * that sharing is **benign**, unlike the `Additional_Comments__c` case above. The two are
       * mutually exclusive by construction: this field needs `could_continue == false`, and the
       * Photos section is gated on `INSTALL_PROCEEDED`, which contains
       * `not { could_continue eq false }`. No state satisfies both, including the unanswered state
       * (`eq false` is false when unanswered, so `not` holds and this field does not). Proven by
       * exhaustive witness search over the controlling fields in
       * `test/forms/schema-defects.test.ts` rather than asserted here.
       *
       * Left as-is deliberately. `Pic_of_why_you_cant_continue_installing__c` — textarea(20000),
       * label "Pic of why you can't continue installing" — exists in the Dev1 describe and is a
       * better semantic fit for this question, but repointing a mapping that loses no data is a
       * change for the owner to want, not a defect to fix. Recorded so it is findable.
       */
      maps: maps(
        'Additional_Photos_if_needed__c',
        'textarea(5000)',
        'Shared with photo_additional, safely: the two can never be visible at once (this needs could_continue = No, photo_additional sits behind INSTALL_PROCEEDED which excludes exactly that). textarea(5000) confirmed on the Dev1 describe 2026-09-03.',
      ),
    },
    {
      id: 'locker_destination',
      kind: 'single',
      label: 'Where is the locker going?',
      options: ['Local Warehouse', 'Central Warehouse'],
      required: true,
      showIf: { field: 'could_continue', op: 'eq', value: false },
      sourceTag: 'Q21',
      maps: guess('Locker_Destination__c', 'A failed install still has to dispose of the hardware and that outcome has no home in the describe. Intervention__c.Ware_House_Location__c is the nearest thing and is on a different object.'),
    },
    {
      id: 'abandon_note',
      kind: 'note',
      label:
        'The source survey ends the response here when the install could not continue: no photos, no anchoring, no provisioning. Everything below is omitted from the submission rather than sent empty.',
      showIf: { field: 'could_continue', op: 'eq', value: false },
    },
  ],
};

/** Everything from here is gated on the install having actually happened. */
const INSTALL_PROCEEDED = {
  all: [DOES_INSTALL, { not: { field: 'could_continue', op: 'eq' as const, value: false } }],
};

const build: Section = {
  id: 'build',
  title: 'As built',
  showIf: INSTALL_PROCEEDED,
  fields: [
    {
      id: 'anchor_type',
      kind: 'multi',
      label: 'What is the anchor type?',
      options: ['Ground side anchor', 'Ground anchor by each foot', 'Wall anchor', 'Freestanding brackets', 'None'],
      required: true,
      prefill: { source: 'history.anchorType', overrideHint: 'This is what the last visit recorded, not what is on the ground. What you built wins.' },
      sourceTag: 'Q24',
      maps: maps('Anchor_Type__c', 'picklist Floor|Wall|Both|No', 'Shape mismatch: the survey collects a five-value multi-select, the field is a four-value single picklist. A collapse rule is required and does not exist yet. C-68 hazard: on a Maintenance record, Anchor_Type__c = No together with Indoor_outdoor__c = Outdoor fires real outbound email through an unguarded flow.'),
    },
    {
      id: 'standing',
      kind: 'single',
      label: 'Is the locker free standing or back to back?',
      options: ['Free Standing', 'Back to Back'],
      required: true,
      prefill: { source: 'kiosk.mountType', overrideHint: 'Correct it if you built it differently from the order.' },
      sourceTag: 'Q25',
      maps: maps('Location_Type__c', 'picklist Back to Back|Free Standing|Wall Mount|Window Mount|Floor Mount', 'OpportunityLineItem uses FREE_STANDING / BACK_TO_BACK; Work_Submission__c uses "Free Standing" / "Back to Back". Normalise on both read and write.'),
    },
    {
      id: 'install_method',
      kind: 'multi',
      label: 'What is the locker installation method?',
      options: [
        'Side Anchor',
        'Anchoring by each foot',
        'Fall Prevention Board',
        'Perforated Skids',
        'On field concrete base',
        'Pre-build concrete base',
        'Wall anchoring',
        'Ground Bracket',
        'Concrete Block',
        'None',
      ],
      required: true,
      prefill: { source: 'case.installationMethod', overrideHint: 'The plan is on the case for 8% of kiosks. What you actually did wins.' },
      sourceTag: 'Q23',
      help:
        'The option list here is the ten values the Salesforce field accepts, not the four in the survey\'s Trash block, so what the technician picks can actually be written.',
      maps: maps('Installation_Method__c', 'multipicklist (10 values)', 'Case.Installation_Method__c has only 6 of these 10 values, so the prefill can supply a value the case could not.'),
    },
    {
      id: 'bolts_installed',
      kind: 'number',
      label: 'How many bolts / wall mounts have you installed?',
      required: true,
      showIf: { field: 'anchor_type', op: 'excludes', value: ['None'] },
      validate: { integer: true, min: 0, max: 200 },
      sourceTag: 'Q30',
      maps: maps('Number_of_bolts_wall_mounts_installed__c', 'double'),
    },
    {
      id: 'side_screws',
      kind: 'number',
      label: 'How many side screws are present?',
      showIf: { field: 'standing', op: 'eq', value: 'Back to Back' },
      validate: { integer: true, min: 0, max: 200 },
      sourceTag: 'Q123',
      maps: maps('Number_of_side_screws_installed__c', 'double'),
    },
    {
      id: 'not_bolted_why',
      kind: 'textarea',
      label: 'Why have you not bolted the locker down?',
      required: true,
      showIf: { field: 'bolts_installed', op: 'eq', value: 0 },
      sourceTag: 'Q31',
      validate: { maxLength: 3000 },
      maps: maps('If_not_anchored_please_provide_image__c', 'url(255)', 'Wrong type — the only "not anchored" field on the object holds a photo URL, not an explanation. The explanation has nowhere to land.'),
    },
    {
      id: 'sticker_correct',
      kind: 'yesno',
      label: 'Has the correct sticker name been installed?',
      required: true,
      sourceTag: 'Q26',
      maps: guess('Correct_Sticker_Installed__c', 'The nearest field is One_or_several_sticker_incompliant__c, a multipicklist on the Maintenance path.'),
    },
    {
      id: 'sticker_why',
      kind: 'text',
      label: 'If the correct sticker name was not installed, why?',
      required: true,
      showIf: { field: 'sticker_correct', op: 'eq', value: false },
      sourceTag: 'Q27',
      maps: guess('Sticker_Issue_Reason__c', 'No matching field found in the describe.'),
    },
    {
      id: 'component_missing',
      kind: 'yesno',
      label: 'Is a component missing from the install?',
      required: true,
      sourceTag: 'Q28',
      maps: guess('Component_Missing__c', 'No matching field found in the describe.'),
    },
    {
      id: 'components_missing_which',
      kind: 'multi',
      label: 'Which component(s) are missing?',
      options: ['Amazon Logo', 'Call to Action', 'CS Sticker', 'Locker Name', 'Floor Anchor', 'Wall Anchor', 'Lightbox', 'Shelter', 'UPS'],
      showIf: { field: 'component_missing', op: 'eq', value: true },
      sourceTag: 'Q29',
      maps: guess('Missing_Components__c', 'No matching field found in the describe.'),
    },
    {
      id: 'visible_damage',
      kind: 'yesno',
      label: 'Is there any visible damage to the locker?',
      required: true,
      sourceTag: 'Q32',
      maps: maps('any_visible_damage_on_the_locker__c', 'multipicklist Yes|No|Scratches|Dents', 'A yes/no question against a four-value multipicklist. Note the near-identical any_visable_damage_on_the_locker__c, whose label is literally "delete" — do not write that one.'),
    },
    {
      id: 'damage_description',
      kind: 'textarea',
      label: 'Describe the damage',
      required: true,
      showIf: { field: 'visible_damage', op: 'eq', value: true },
      sourceTag: 'Q102',
      validate: { maxLength: 10000 },
      maps: maps('describe_damage_location__c', 'textarea(10000)'),
    },
    {
      id: 'damage_photo',
      kind: 'photo',
      label: 'Photo of the damage',
      required: true,
      showIf: { field: 'visible_damage', op: 'eq', value: true },
      validate: { minPhotos: 1, maxPhotos: 5 },
      sourceTag: 'Q33',
      maps: maps('Take_photos_of_damage__c', 'url(255)'),
    },
    {
      id: 'seismic_trap',
      kind: 'yesno',
      label: 'Have you installed a seismic trap?',
      required: true,
      showIf: { field: 'placement', op: 'eq', value: 'Indoor' },
      sourceTag: 'Q34',
      maps: guess('Seismic_Trap_Installed__c', 'No matching field found in the describe.'),
    },
    {
      id: 'shelter_or_lightbox',
      kind: 'yesno',
      label: 'Have you installed a shelter or lightbox?',
      showIf: {
        all: [
          { field: 'placement', op: 'eq', value: 'Outdoor' },
          { field: 'generation', op: 'notIn', value: ['5.0'] },
        ],
      },
      sourceTag: 'Q102',
      maps: maps('Shelter_installed__c', 'picklist Yes|No'),
    },
    {
      id: 'electrical_available',
      kind: 'yesno',
      label: 'Is the electrical connection available?',
      required: true,
      showIf: { field: 'generation', op: 'notIn', value: ['4.0'] },
      sourceTag: 'Q35',
      maps: maps('Indoor_Electrical_outlet__c', 'string(255)'),
    },
    {
      id: 'no_electrical_reason',
      kind: 'single',
      label: 'Why is there no electrical connection?',
      options: ['Not installed by host', 'Wrong position', 'Not energised', 'Other'],
      required: true,
      showIf: { field: 'electrical_available', op: 'eq', value: false },
      inferred: true,
      help: 'Not in the source survey. Added because the field exists with a four-value picklist and the survey records the failure without its reason.',
      maps: maps('Reason_for_no_electrical_connection__c', 'picklist (4 values)', 'The four real picklist values were not captured; the options here are placeholders and must be replaced from the describe before this writes.'),
    },
    {
      id: 'screen_illuminated',
      kind: 'yesno',
      label: 'Is the locker screen illuminated?',
      required: true,
      showIf: { field: 'generation', op: 'notIn', value: ['4.0'] },
      sourceTag: 'Q36',
      maps: maps('Is_the_locker_screen_illuminated__c', 'picklist', 'Verified through the Maintenance workflow mapping in discovery/qualtrics/10-WORKFLOWS-AND-SF-MAPPING.md.'),
    },
  ],
};

const photos: Section = {
  id: 'photos',
  title: 'Photos',
  subtitle: 'Nine required frames, plus the anchoring set for whatever you actually built.',
  showIf: INSTALL_PROCEEDED,
  fields: [
    { id: 'photo_storefront', kind: 'photo', label: 'Front view of the store', required: true, sourceTag: 'Q39', maps: guess('Store_Front_Photo__c', 'No storefront field found in the describe.') },
    { id: 'photo_panoramic', kind: 'photo', label: 'Panoramic picture of the locker from a distance', required: true, sourceTag: 'Q40', maps: maps('Locker_Center_View__c', 'url') },
    { id: 'photo_front', kind: 'photo', label: 'Front of the locker', required: true, sourceTag: 'Q41', maps: maps('Please_provide_a_picture_of_the_front__c', 'url(255)') },
    { id: 'photo_right', kind: 'photo', label: 'Right side of the locker', required: true, sourceTag: 'Q44', maps: maps('Please_provide_a_picture_of_the_right__c', 'url(255)') },
    { id: 'photo_left', kind: 'photo', label: 'Left side of the locker', required: true, sourceTag: 'Q45', maps: maps('Please_provide_a_picture_of_the_left__c', 'url(255)') },
    {
      id: 'photo_wall_gap',
      kind: 'photo',
      label: 'Gap between the locker and the wall',
      required: true,
      showIf: { field: 'anchor_type', op: 'excludes', value: ['Wall anchor'] },
      sourceTag: 'Q46',
      maps: guess('Wall_Gap_Photo__c', 'No wall-gap field found in the describe.'),
    },
    { id: 'photo_name_sticker', kind: 'photo', label: 'Locker name sticker', required: true, sourceTag: 'Q42', maps: maps('Photo_of_the_locker_name__c', 'textarea(2000)') },
    {
      id: 'photo_right_anchor',
      kind: 'photo',
      label: 'Right ground anchoring',
      required: true,
      showIf: { field: 'placement', op: 'eq', value: 'Outdoor' },
      sourceTag: 'Q47',
      maps: maps('Photo_of_right_anchor__c', 'url(255)'),
    },
    {
      id: 'photo_left_anchor',
      kind: 'photo',
      label: 'Left ground anchoring',
      required: true,
      showIf: { field: 'placement', op: 'eq', value: 'Outdoor' },
      sourceTag: 'Q48',
      maps: maps('Photo_of_left_anchor__c', 'url(255)'),
    },
    {
      id: 'photo_left_wall_anchor',
      kind: 'photo',
      label: 'Left wall anchoring',
      required: true,
      showIf: {
        all: [
          { field: 'placement', op: 'eq', value: 'Outdoor' },
          { field: 'standing', op: 'eq', value: 'Back to Back' },
        ],
      },
      sourceTag: 'Q49',
      maps: maps('Photo_from_the_left_wall_anchor__c', 'url(255)'),
    },
    {
      id: 'photo_right_wall_anchor',
      kind: 'photo',
      label: 'Right wall anchoring',
      required: true,
      showIf: {
        all: [
          { field: 'placement', op: 'eq', value: 'Outdoor' },
          { field: 'standing', op: 'eq', value: 'Back to Back' },
        ],
      },
      sourceTag: 'Q51',
      maps: maps('Photo_from_the_right_wall_anchor__c', 'url(255)'),
    },
    {
      id: 'photo_intermediate_anchor',
      kind: 'photo',
      label: 'Intermediate anchoring between columns',
      help: 'One frame per join. A five-column locker has four joins.',
      showIf: {
        all: [
          { field: 'standing', op: 'eq', value: 'Free Standing' },
          { field: 'ctx.intermediateAnchorCount', op: 'gte', value: 1 },
        ],
      },
      validate: { maxPhotos: 5 },
      sourceTag: 'Q106',
      maps: maps('Photo_from_the_intermediate_anchorings_1__c', 'url(255)', 'The object has five numbered slots (…_1__c … _5__c); this one field fans out to them on write, and a locker with more than five joins cannot be fully recorded.'),
    },
    {
      id: 'photo_modem_serial',
      kind: 'photo',
      label: 'Modem serial number',
      required: true,
      showIf: { field: 'generation', op: 'notIn', value: ['4.0'] },
      sourceTag: 'Q104',
      maps: maps('Photo_of_modem_serial_number__c', 'url(255)'),
    },
    {
      id: 'modem_mac',
      kind: 'text',
      label: 'MAC address of the modem',
      help: 'The record already holds one for 78% of kiosks. Confirm it or correct it rather than reading the label from scratch.',
      prefill: { source: 'kiosk.macAddress', overrideHint: 'Correct it if the modem has been swapped.' },
      inferred: true,
      maps: maps('Modem_serial_number__c', 'textarea', 'Verified through the Maintenance workflow mapping. Not asked by the install survey, which only photographs the label.'),
    },
    { id: 'photo_sim_serial', kind: 'photo', label: 'SIM serial number', sourceTag: 'Q105', maps: maps('Photo_of_SIM_serial_number__c', 'url(255)') },
    {
      id: 'photo_additional',
      kind: 'photo',
      label: 'Any additional photo that may be needed',
      validate: { maxPhotos: 10 },
      sourceTag: 'Q53',
      // The other half of the benign `Additional_Photos_if_needed__c` pair. See `blocked_photo`.
      maps: maps(
        'Additional_Photos_if_needed__c',
        'textarea(5000)',
        'Shared with blocked_photo, safely: this section is gated on INSTALL_PROCEEDED, which excludes the could_continue = No state that blocked_photo requires. Mutual exclusion is proven by witness search in test/forms/schema-defects.test.ts.',
      ),
    },
  ],
};

const serials: Section = {
  id: 'serials',
  title: 'Module serials',
  subtitle:
    'One row per column, derived from the record. Serials already on the asset records are shown to compare against but are never adopted as your answer — roughly 60% of them are migration placeholders.',
  showIf: INSTALL_PROCEEDED,
  fields: [
    {
      id: 'serial_note',
      kind: 'note',
      label:
        'Scan where you can. A scanned serial and a typed serial are recorded differently, because they are worth different amounts when a serial is later disputed.',
    },
    ...moduleSerialFields(),
  ],
};

const provisioning: Section = {
  id: 'provisioning',
  title: 'Provisioning',
  subtitle: 'Reached directly on a Provisioning-only visit, or after the install otherwise.',
  showIf: DOES_PROVISIONING,
  fields: [
    {
      id: 'provisioning_code',
      kind: 'text',
      label: 'Provisioning code',
      help: 'On the intervention for 14% of visits. Treated as a secret: never logged.',
      prefill: { source: 'intervention.provisioningCode' },
      inferred: true,
      maps: maps('Provisioning_Code__c', 'string(255)'),
    },
    {
      id: 'provisioned',
      kind: 'yesno',
      label: 'Has the locker been provisioned?',
      required: true,
      sourceTag: 'Q57',
      maps: maps('Is_the_locker_provisioned__c', 'picklist Yes|No'),
    },
    {
      id: 'provisioning_photo',
      kind: 'photo',
      label: 'Photo of the provisioning',
      required: true,
      showIf: { field: 'provisioned', op: 'eq', value: true },
      sourceTag: 'Q58',
      maps: maps('Provisioning_Screen_Picture__c', 'textarea(5000)'),
    },
    {
      id: 'provisioning_failure',
      kind: 'single',
      label: 'If not provisioned, why?',
      options: [
        'Amazon Related - System unavailable error',
        'Amazon Related - Credentials failed',
        'Amazon Related - Re-Image Needed',
        'Host Related - Data ports not setup',
        'Host Related - Firewall',
        'Host Related - Network not detected',
        'Host Related - No power/final connection not completed',
        'Host Related - Location Not Accessible',
        'Supplier Related - Problem with setup error',
        'Supplier Related - Improper tools/equipment on site',
        'Supplier Related - Abandoned',
        'Supplier Related - Technician Error',
        'Hardware - Control Board',
        'Hardware - Screen',
        'Hardware - IPC',
        'Hardware - Modem/Router',
        'Hardware - Antennas',
        'Hardware - Other',
        'Force Majeure',
      ],
      required: true,
      showIf: { field: 'provisioned', op: 'eq', value: false },
      sourceTag: 'Q59',
      help: 'The richest single field in the source survey: a real structured failure taxonomy, not a free-text box.',
      maps: maps('Provisioning_failure_reason__c', 'picklist (24 values)', 'The field has 24 values and the survey offers 19, worded differently ("Hardware - Screen" versus "Hardware / Parts - Screen"). A normalisation map is required and does not exist yet. The survey wording is kept here because it is what technicians read today.'),
    },
    {
      id: 'provisioning_hardware_other',
      kind: 'text',
      label: 'What other hardware / part was the issue?',
      required: true,
      showIf: { field: 'provisioning_failure', op: 'eq', value: 'Hardware - Other' },
      sourceTag: 'Q102',
      maps: maps('Parts_preventing_provisioning__c', 'string(255)'),
    },
    {
      id: 'spoke_to_ots',
      kind: 'yesno',
      label: 'Have you spoken to OTS?',
      required: true,
      showIf: { field: 'provisioned', op: 'eq', value: false },
      sourceTag: 'Q60',
      maps: guess('Spoke_To_OTS__c', 'The Maintenance path has Did_You_try_to_contact_with_OTS_suppor__c; no install-side equivalent was found.'),
    },
    {
      id: 'extra_parts_needed',
      kind: 'yesno',
      label: 'Were additional parts needed to complete provisioning?',
      required: true,
      showIf: { field: 'provisioned', op: 'eq', value: false },
      sourceTag: 'Q62',
      maps: maps('additional_parts_needed_to_provision__c', 'picklist Yes|No'),
    },
    {
      id: 'extra_parts_which',
      kind: 'textarea',
      label: 'Which parts are needed?',
      required: true,
      showIf: { field: 'extra_parts_needed', op: 'eq', value: true },
      inferred: true,
      validate: { maxLength: 20000 },
      maps: maps('Parts_needed_to_provision__c', 'textarea(20000)'),
    },
    {
      id: 'phone_connected',
      kind: 'yesno',
      label: 'Did your phone connect to the locker?',
      required: true,
      showIf: {
        all: [
          { field: 'provisioned', op: 'eq', value: false },
          { field: 'generation', op: 'eq', value: '4.0' },
        ],
      },
      sourceTag: 'Q66',
      maps: guess('Phone_Connected_To_Locker__c', 'No matching field found in the describe.'),
    },
    {
      id: 'odin_app_version',
      kind: 'text',
      label: 'What version of the ODIN app are you using?',
      required: true,
      showIf: {
        all: [
          { field: 'provisioned', op: 'eq', value: false },
          { field: 'generation', op: 'eq', value: '4.0' },
        ],
      },
      sourceTag: 'Q63',
      maps: maps('Version_of_Odin_provisioning_app_used__c', 'string(255)'),
    },
    {
      id: 'provisioning_not_done_why',
      kind: 'textarea',
      label: 'Anything else about why it is not provisioned',
      showIf: { field: 'provisioned', op: 'eq', value: false },
      inferred: true,
      validate: { maxLength: 2000 },
      maps: maps('If_no_describe_the_issue__c', 'textarea(2000)', 'Field label on the object is "Why has the locker not been provisioned".'),
    },
  ],
};

const closeOut: Section = {
  id: 'close',
  title: 'Close out',
  fields: [
    {
      id: 'comments',
      kind: 'textarea',
      label: 'Any additional comments that may be needed',
      sourceTag: 'Q54',
      validate: { maxLength: 3000 },
      maps: maps('Additional_Comments__c', 'textarea(3000)', 'Describe says 3000; infra declares 32768. The stricter limit wins here.'),
    },
    {
      id: 'visit_end',
      kind: 'time',
      label: 'Visit end time (HH:MM, 24-hour)',
      required: true,
      validate: { pattern: '([01][0-9]|2[0-3]):[0-5][0-9]', patternMessage: 'Use HH:MM, 24-hour' },
      sourceTag: 'Q55/Q56',
      help: 'The source survey has two of these, one per terminating branch. One field, both branches.',
      maps: maps('Visit_End_Time__c', 'string(30)'),
    },
  ],
};

export const INSTALL: FormSchema = {
  id: 'install',
  version: 1,
  title: 'Install',
  source: {
    provenance: 'sourced',
    artefact: 'Installation_Form_FIXED.qsf — Qualtrics SV_8G1669b6MfCq5IG "Installation Form", owner-supplied, last modified 2026-09-01',
    sourceQuestionCount: 106,
    note:
      'Question text, choices, required-ness and display logic taken from the export. Section order follows the Survey Flow rather than the block order. The 20-question Trash block is not reproduced. Two fields are marked inferred: an electrical-failure reason and a modem MAC confirmation, both added because the Salesforce field exists and the survey collects the outcome without it.',
  },
  destination: {
    object: 'Work_Submission__c',
    recordTypeLabel: 'Installation',
    confidence: 'verified',
    evidence:
      'Work_Submission__c has an active Installation record type carrying 49,261 rows (discovery/salesforce/field-dumps.txt record types; discovery/airtable/20-FORMS-INVENTORY.md). The record type is verified; the per-question field mapping is not — see the note on each field.',
  },
  sections: [identity, beforeWork, blocked, build, photos, serials, provisioning, closeOut],
};

/** Referenced by the schema index only; kept exported so the fan-out is testable. */
export { moduleSerialFields, IS_PROVISIONING_ONLY };
export type { Section as InstallSection };
